import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { AppComponent } from 'src/app/app.component';
import {
  Designation,
  MenuRightNode,
  SaveDesignationRightRequest,
} from './designationrights.model';
import { AuthService } from 'src/app/API/auth.service';
import { ContactApiService } from '../../API/contactapi.service';

@Component({
  selector: 'app-designationrights',
  templateUrl: './designationrights.component.html',
  styleUrls: ['./designationrights.component.css'],
})
export class DesignationRightsComponent implements OnInit {
  designations: Designation[] = [];
  selectedDesignationId: any = "";

  tree: MenuRightNode[] = [];
  loading = false;
  saving = false;
  search = '';

  constructor(private api: AuthService,private capi:ContactApiService) {}

  ngOnInit(): void {
    this.loading = true;
    this.capi.FillDesignation()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (d) => (this.designations = d || []),
        error: () => (this.designations = []),
      });
  }

  loadRights(): void {
    if (!this.selectedDesignationId) {
      this.tree = [];
      return;
    }

    this.loading = true;
    AppComponent.loading = true;
    this.api
      .GetDesignationRights(this.selectedDesignationId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          AppComponent.loading = false;
          this.tree = res.data || [];
        },
        error: () => {
          AppComponent.loading = false;
          AppComponent.addAlert('Failed to load rights', 'error');
          //alert('Failed to load rights');
          this.tree = [];
        },
      });
  }

  // rules:
  // - if any action checked => visible true
  // - if visible unchecked => all others false
  onToggle(node: MenuRightNode, which: 'visible' | 'create' | 'edit' | 'addnotes' | 'delete'): void {
    if (which === 'visible') {
      if (!node.canVisible) {
        node.canCreate = false;
        node.canEdit = false;
        node.canAddNotes = false;
        node.canDelete = false;
      }
    } else {
      if (node.canCreate || node.canEdit || node.canAddNotes || node.canDelete) node.canVisible = true;
      if (!node.canVisible) {
        node.canCreate = false;
        node.canEdit = false;
        node.canAddNotes = false;
        node.canDelete = false;
      }
    }

    // optional: update roleActions string on UI (not required for save)
    node.roleActions = this.buildActions(node);
  }

  private buildActions(n: MenuRightNode): string {
    // normalize
    if (n.canCreate || n.canEdit || n.canAddNotes || n.canDelete) n.canVisible = true;
    if (!n.canVisible) return '00000';

    return `${n.canVisible ? '1' : '0'}${n.canCreate ? '1' : '0'}${n.canEdit ? '1' : '0'}${n.canAddNotes ? '1' : '0'}${n.canDelete ? '1' : '0'}`;
  }

  // Apply current node rights to all descendants
  applyToChildren(node: MenuRightNode): void {
    if (!node.children?.length) return;

    for (const c of node.children) {
      c.canVisible = node.canVisible;
      c.canCreate = node.canCreate;
      c.canEdit = node.canEdit;
      c.canAddNotes = node.canAddNotes;
      c.canDelete = node.canDelete;
      c.roleActions = this.buildActions(c);

      this.applyToChildren(c);
    }
  }

  save(): void {
    if (!this.selectedDesignationId) return;

    const rows: SaveDesignationRightRequest[] = [];
    this.flatten(this.tree, rows);
    AppComponent.loading = true;
    this.saving = true;
    this.api
      .SaveDesignationRights(rows)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (res) => {
          AppComponent.loading = false;
          if (!res?.transaction?.returnStatus) {
            var message = ((res?.transaction?.returnMessage || []).join('\n') || 'Save failed');
            AppComponent.addAlert(message, 'error');
            
            return;
          }
          var message = (res?.transaction?.returnMessage || ['Saved']).join('\n');
          AppComponent.addAlert(message, 'success');
        },
        error: () => {
          AppComponent.addAlert('Save failed', 'error');
          AppComponent.loading = false;
        },
      });
  }

  private flatten(nodes: MenuRightNode[], rows: SaveDesignationRightRequest[]): void {
    for (const n of nodes) {
      rows.push({
        designationId: this.selectedDesignationId!,
        menuId: n.menuId,
        canVisible: !!n.canVisible,
        canCreate: !!n.canCreate,
        canEdit: !!n.canEdit,
        canAddNotes: !!n.canAddNotes,
        canDelete: !!n.canDelete,
      });

      if (n.children?.length) this.flatten(n.children, rows);
    }
  }

  selectAll(): void {
  this.applyBulk(this.tree, true);
}

deselectAll(): void {
  this.applyBulk(this.tree, false);
}

private applyBulk(nodes: MenuRightNode[], on: boolean): void {
  for (const n of nodes) {
    n.canVisible = on;
    n.canCreate = on;
    n.canEdit = on;
    n.canAddNotes = on;
    n.canDelete = on;

    // keep roleActions in sync (optional but nice)
    n.roleActions = on ? '11111' : '00000';

    if (n.children?.length) {
      this.applyBulk(n.children, on);
    }
  }
}

  // Simple filter: hide nodes not matching text unless any child matches
  get filteredTree(): MenuRightNode[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.tree;

    const filter = (nodes: MenuRightNode[]): MenuRightNode[] => {
      const out: MenuRightNode[] = [];
      for (const n of nodes) {
        const selfMatch = (n.menuDesc || '').toLowerCase().includes(q);
        const childMatch = n.children?.length ? filter(n.children) : [];
        if (selfMatch || childMatch.length) out.push({ ...n, children: childMatch });
      }
      return out;
    };

    return filter(this.tree);
  }
}
